import React, { useState, useEffect } from "react"
import MetaTags from "react-meta-tags"
import {
    Card,
    CardBody,
    Col,
    Container,
    Row,
    Nav,
    NavItem,
    NavLink,
    TabContent,
    TabPane,
    Button,
    Modal,
    ModalHeader,
    ModalBody,
    Form,
    FormGroup,
    Label,
    Input,
    Table
} from "reactstrap"
import classnames from "classnames"
import Datatables from "components/Datatables/datatable"
import Select from "react-select"
import Flatpickr from "react-flatpickr"
import { get, post, del } from "helpers/api_helper"
import Notifications from "components/Notifications"
import {
    ROSTER_SHIFTS_URL,
    ROSTER_RULES_URL,
    ROSTER_URL,
    ROSTER_GRID_URL
} from "helpers/url_helper"
import "flatpickr/dist/themes/material_green.css"

const Roster = () => {
    const [activeTab, setActiveTab] = useState("1")
    const [isLoading, setIsLoading] = useState(false)
    const [isActionLoading, setIsActionLoading] = useState(false)

    // Selectors options
    const [branchOptions, setBranchOptions] = useState([])
    const [employeeOptions, setEmployeeOptions] = useState([])
    const [shiftOptions, setShiftOptions] = useState([])

    // Branch filter for grid and list
    const [filters, setFilters] = useState({
        branch_id: "",
        employee_id: "all",
        year: new Date().getFullYear(),
        month: new Date().getMonth() + 1
    })

    // Grid states
    const [gridData, setGridData] = useState({ employees: [], shifts: [], grid: {} })

    // List states
    const [listData, setListData] = useState({ rows: [], total_rows: 0 })
    const [listPagination, setListPagination] = useState({ limit: 10, page: 1, search: "" })
    const [listFilters, setListFilters] = useState({
        branch_id: "",
        employee_id: "all",
        start_date: "",
        end_date: ""
    })

    // Shift Master states
    const [shiftsData, setShiftsData] = useState({ rows: [], total_rows: 0 })
    const [shiftModal, setShiftModal] = useState(false)
    const [editShiftId, setEditShiftId] = useState(null)
    const [shiftForm, setShiftForm] = useState({
        shift_name: "",
        start_time: "09:00:00",
        end_time: "18:00:00",
        actual_duration: "9.00",
        min_hours: "8.00",
        cross_day: 0,
        shift_priority: 1,
        allowed_combination: "",
        status: 1
    })

    // Rules states
    const [rules, setRules] = useState({
        min_shift_hours: "10",
        min_rest_hours: "10",
        max_continuous_hours: "20",
        monthly_required_hours: "240",
        allow_double_shift: "1",
        mandatory_rest_after_double_shift: "1"
    })

    // Assignment Modal (Single/Bulk)
    const [assignModal, setAssignModal] = useState(false)
    const [isBulkAssign, setIsBulkAssign] = useState(false)
    const [assignForm, setAssignForm] = useState({
        id: null, // for update
        employee_id: "",
        employee_ids: [],
        shift_id: "",
        shift_date: "",
        start_date: "",
        end_date: "",
        status: "Assigned",
        is_double_shift: 0
    })

    // Common Button Styles
    const brownBtnStyle = { backgroundColor: "#3d1e08", borderColor: "#3d1e08", color: "#fff" }
    const greenBtnStyle = { backgroundColor: "#159858", borderColor: "#159858", color: "#fff" }

    // Styles for react-select
    const customStyles = {
        control: (base) => ({
            ...base,
            height: 38,
            minHeight: 38
        })
    }

    // Pad number
    const pad = (num) => String(num).padStart(2, "0")

    // Fetch Selectors (Branches, Employees, Shifts)
    const fetchSelectors = async () => {
        try {
            const branchRes = await get("api/selectors/getBranchs")
            if (branchRes.status === 200) {
                setBranchOptions(branchRes.response.map(b => ({ value: b.id, label: b.title })))
            }
            const empRes = await post("api/selectors/getEmployees", { branch: "all" })
            if (empRes.status === 200) {
                setEmployeeOptions(empRes.response)
            }
            const shiftsRes = await get(ROSTER_SHIFTS_URL)
            if (shiftsRes.status === 200) {
                const rows = shiftsRes.response.rows || []
                setShiftOptions(rows.map(s => ({ value: s.id, label: `${s.shift_name} (${s.start_time}-${s.end_time})` })))
                setShiftsData({ rows, total_rows: shiftsRes.response.total_rows || rows.length })
            }
        } catch (e) {
            console.error("Selectors fetch error:", e)
        }
    }

    // Grid Data Fetch
    const getGridData = async () => {
        setIsLoading(true)
        try {
            const daysInMonth = new Date(filters.year, filters.month, 0).getDate()
            const start_date = `${filters.year}-${pad(filters.month)}-01`
            const end_date = `${filters.year}-${pad(filters.month)}-${pad(daysInMonth)}`

            const queryParams = new URLSearchParams({
                start_date,
                end_date,
                ...(filters.branch_id && { branch_id: filters.branch_id }),
                ...(filters.employee_id && filters.employee_id !== "all" && { employee_id: filters.employee_id })
            }).toString()

            const res = await get(`${ROSTER_GRID_URL}?${queryParams}`)
            if (res.status === 200) {
                setGridData(res.response)
            }
        } catch (e) {
            console.error("Grid fetch error:", e)
        }
        setIsLoading(false)
    }

    // Roster List Fetch
    const getListData = async () => {
        setIsLoading(true)
        try {
            const queryParams = new URLSearchParams({
                page: listPagination.page,
                limit: listPagination.limit,
                search: listPagination.search,
                ...(listFilters.branch_id && { branch_id: listFilters.branch_id }),
                ...(listFilters.employee_id && listFilters.employee_id !== "all" && { employee_id: listFilters.employee_id }),
                ...(listFilters.start_date && { start_date: listFilters.start_date }),
                ...(listFilters.end_date && { end_date: listFilters.end_date })
            }).toString()

            const res = await get(`${ROSTER_URL}?${queryParams}`)
            if (res.status === 200) {
                setListData({
                    rows: res.response.rows || [],
                    total_rows: res.response.total_rows || 0
                })
            }
        } catch (e) {
            console.error("List fetch error:", e)
        }
        setIsLoading(false)
    }

    // Shift Master Fetch
    const getShiftsData = async () => {
        setIsLoading(true)
        try {
            const res = await get(ROSTER_SHIFTS_URL)
            if (res.status === 200) {
                const rows = res.response.rows || []
                setShiftOptions(rows.map(s => ({ value: s.id, label: `${s.shift_name} (${s.start_time}-${s.end_time})` })))
                setShiftsData({
                    rows,
                    total_rows: res.response.total_rows || rows.length
                })
            }
        } catch (e) {
            console.error("Shifts fetch error:", e)
        }
        setIsLoading(false)
    }

    // Rules Fetch
    const getRulesData = async () => {
        setIsLoading(true)
        try {
            const res = await get(ROSTER_RULES_URL)
            if (res.status === 200) {
                setRules(res.response)
            }
        } catch (e) {
            console.error("Rules fetch error:", e)
        }
        setIsLoading(false)
    }

    // Trigger Loads based on Active Tab
    useEffect(() => {
        fetchSelectors()
    }, [])

    useEffect(() => {
        if (activeTab === "1") {
            getGridData()
        } else if (activeTab === "2") {
            getListData()
        } else if (activeTab === "3") {
            getShiftsData()
        } else if (activeTab === "4") {
            getRulesData()
        }
    }, [activeTab, filters, listPagination, listFilters])

    // Toggle Modal
    const toggleShiftModal = () => {
        setShiftModal(!shiftModal)
        if (!shiftModal) {
            setEditShiftId(null)
            setShiftForm({
                shift_name: "",
                start_time: "09:00:00",
                end_time: "18:00:00",
                actual_duration: "9.00",
                min_hours: "8.00",
                cross_day: 0,
                shift_priority: 1,
                allowed_combination: "",
                status: 1
            })
        }
    }

    const toggleAssignModal = (bulk = false) => {
        setAssignModal(!assignModal)
        setIsBulkAssign(bulk)
        if (!assignModal) {
            setAssignForm({
                id: null,
                employee_id: "",
                employee_ids: [],
                shift_id: "",
                shift_date: "",
                start_date: "",
                end_date: "",
                status: "Assigned",
                is_double_shift: 0
            })
        }
    }

    // CRUD Shift Master
    const handleShiftSubmit = async (e) => {
        e.preventDefault()
        setIsActionLoading(true)
        try {
            const url = editShiftId ? `${ROSTER_SHIFTS_URL}/update/${editShiftId}` : ROSTER_SHIFTS_URL
            const res = await post(url, shiftForm)
            if (res.status === 200 || res.status === true) {
                Notifications(editShiftId ? "Shift updated successfully!" : "Shift created successfully!")
                toggleShiftModal()
                getShiftsData()
            } else {
                Notifications(res.message || "Failed to save shift", "error")
            }
        } catch (error) {
            Notifications("Error saving shift", "error")
        }
        setIsActionLoading(false)
    }

    const handleShiftEdit = (shift) => {
        setEditShiftId(shift.id)
        setShiftForm({
            shift_name: shift.shift_name,
            start_time: shift.start_time,
            end_time: shift.end_time,
            actual_duration: shift.actual_duration,
            min_hours: shift.min_hours,
            cross_day: parseInt(shift.cross_day) || 0,
            shift_priority: shift.shift_priority,
            allowed_combination: shift.allowed_combination || "",
            status: parseInt(shift.status) || 1
        })
        setShiftModal(true)
    }

    const handleShiftDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this shift?")) return
        setIsActionLoading(true)
        try {
            const res = await del(`${ROSTER_SHIFTS_URL}/${id}`)
            if (res.status === 200 || res.status === true) {
                Notifications("Shift deleted successfully!")
                getShiftsData()
            } else {
                Notifications(res.message || "Failed to delete shift", "error")
            }
        } catch (error) {
            Notifications("Error deleting shift", "error")
        }
        setIsActionLoading(false)
    }

    // CRUD Roster Assignment
    const handleAssignSubmit = async (e) => {
        e.preventDefault()
        setIsActionLoading(true)
        try {
            const payload = { ...assignForm }
            if (isBulkAssign) {
                payload.employee_ids = assignForm.employee_ids.map(emp => emp.value)
            } else {
                payload.employee_id = assignForm.employee_id?.value || assignForm.employee_id
            }
            payload.shift_id = assignForm.shift_id?.value || assignForm.shift_id

            const res = await post(ROSTER_URL, payload)
            if (res.status === 200 || res.status === true) {
                Notifications(res.message || "Shift assigned successfully!")
                toggleAssignModal()
                if (activeTab === "1") getGridData()
                else getListData()
            } else {
                Notifications(res.message || "Failed to assign shift", "error")
            }
        } catch (error) {
            Notifications("Error assigning shift", "error")
        }
        setIsActionLoading(false)
    }

    const handleRosterDelete = async (id) => {
        if (!window.confirm("Are you sure you want to unassign this shift?")) return
        setIsActionLoading(true)
        try {
            const res = await del(`${ROSTER_URL}/${id}`)
            if (res.status === 200 || res.status === true) {
                Notifications("Shift unassigned successfully!")
                if (activeTab === "1") getGridData()
                else getListData()
            } else {
                Notifications(res.message || "Failed to unassign shift", "error")
            }
        } catch (error) {
            Notifications("Error unassigning shift", "error")
        }
        setIsActionLoading(false)
    }

    // Rules Update
    const handleRulesSubmit = async (e) => {
        e.preventDefault()
        setIsActionLoading(true)
        try {
            const res = await post(ROSTER_RULES_URL, rules)
            if (res.status === 200 || res.status === true) {
                Notifications("Rules updated successfully!")
                getRulesData()
            } else {
                Notifications(res.message || "Failed to update rules", "error")
            }
        } catch (error) {
            Notifications("Error updating rules", "error")
        }
        setIsActionLoading(false)
    }

    // Quick assignment from grid cell click
    const handleGridCellClick = (employee, dateStr, existingRoster) => {
        if (existingRoster && existingRoster.roster_id) {
            if (window.confirm(`Unassign ${existingRoster.shift_name} shift for ${employee.name} on ${dateStr}?`)) {
                handleRosterDelete(existingRoster.roster_id)
            }
        } else {
            setAssignForm({
                id: null,
                employee_id: { value: employee.code, label: employee.name },
                employee_ids: [],
                shift_id: 1,
                shift_date: dateStr,
                start_date: "",
                end_date: "",
                status: "Assigned",
                is_double_shift: 0
            })
            setIsBulkAssign(false)
            setAssignModal(true)
        }
    }

    // Grid Generation Helpers
    const getDaysArray = () => {
        const daysInMonth = new Date(filters.year, filters.month, 0).getDate()
        return Array.from({ length: daysInMonth }, (_, i) => i + 1)
    }

    const getShiftBadgeStyle = (shiftName) => {
        const name = shiftName?.toLowerCase() || ""
        if (name.includes("morning") || name.includes("day")) {
            return { backgroundColor: "#e3f2fd", color: "#0d47a1", border: "1px solid #bbdefb", fontSize: "11px", padding: "2px 6px", borderRadius: "4px", fontWeight: "bold" }
        }
        if (name.includes("night")) {
            return { backgroundColor: "#efebe9", color: "#3e2723", border: "1px solid #d7ccc8", fontSize: "11px", padding: "2px 6px", borderRadius: "4px", fontWeight: "bold" }
        }
        if (name.includes("evening")) {
            return { backgroundColor: "#f3e5f5", color: "#4a148c", border: "1px solid #e1bee7", fontSize: "11px", padding: "2px 6px", borderRadius: "4px", fontWeight: "bold" }
        }
        if (name.includes("off") || name.includes("weekly")) {
            return { backgroundColor: "#eceff1", color: "#37474f", border: "1px solid #cfd8dc", fontSize: "11px", padding: "2px 6px", borderRadius: "4px", fontWeight: "bold" }
        }
        if (name.includes("leave")) {
            return { backgroundColor: "#fff3e0", color: "#e65100", border: "1px solid #ffe0b2", fontSize: "11px", padding: "2px 6px", borderRadius: "4px", fontWeight: "bold" }
        }
        return { backgroundColor: "#e8f5e9", color: "#1b5e20", border: "1px solid #c8e6c9", fontSize: "11px", padding: "2px 6px", borderRadius: "4px", fontWeight: "bold" }
    }

    // Columns definitions
    const listColumns = [
        {
            dataField: "id",
            text: "#",
            formatter: (cell, row, index) => (listPagination.page - 1) * listPagination.limit + index + 1
        },
        { dataField: "employee_name", text: "EMPLOYEE", sort: true },
        { dataField: "employee_code", text: "CODE", sort: true },
        { dataField: "shift_date", text: "DATE", sort: true },
        { dataField: "shift_name", text: "SHIFT", sort: true },
        {
            dataField: "start_time",
            text: "TIMINGS",
            formatter: (cell, row) => `${row.start_time} - ${row.end_time}`
        },
        {
            dataField: "status",
            text: "STATUS",
            formatter: (cell) => (
                <span className={`badge ${cell === "Assigned" ? "bg-success" : cell === "Leave" ? "bg-warning" : "bg-secondary"}`}>{cell}</span>
            )
        },
        {
            dataField: "action",
            text: "ACTION",
            formatter: (cell, row) => (
                <>
                    <Button color="light" size="sm" className="me-1" onClick={() => {
                        setAssignForm({
                            id: row.id,
                            employee_id: { value: row.employee_id, label: row.employee_name },
                            shift_id: { value: row.shift_id, label: row.shift_name },
                            shift_date: row.shift_date,
                            status: row.status,
                            is_double_shift: parseInt(row.is_double_shift) || 0
                        })
                        setIsBulkAssign(false)
                        setAssignModal(true)
                    }}><i className="far fa-edit text-primary"></i></Button>
                    <Button color="light" size="sm" onClick={() => handleRosterDelete(row.id)}><i className="far fa-times-circle text-danger"></i></Button>
                </>
            )
        }
    ]

    const shiftMasterColumns = [
        { dataField: "id", text: "#", formatter: (cell, row, index) => index + 1 },
        { dataField: "shift_name", text: "SHIFT NAME", sort: true },
        { dataField: "start_time", text: "START TIME" },
        { dataField: "end_time", text: "END TIME" },
        { dataField: "actual_duration", text: "DURATION (HRS)" },
        { dataField: "min_hours", text: "MIN HOURS" },
        {
            dataField: "cross_day",
            text: "CROSS DAY?",
            formatter: (cell) => cell ? "Yes" : "No"
        },
        { dataField: "shift_priority", text: "PRIORITY" },
        {
            dataField: "status",
            text: "STATUS",
            formatter: (cell) => cell ? <span className="badge bg-success">Active</span> : <span className="badge bg-danger">Inactive</span>
        },
        {
            dataField: "action",
            text: "ACTION",
            formatter: (cell, row) => (
                <>
                    <Button color="light" size="sm" className="me-1" onClick={() => handleShiftEdit(row)}><i className="far fa-edit text-primary"></i></Button>
                    <Button color="light" size="sm" onClick={() => handleShiftDelete(row.id)}><i className="far fa-times-circle text-danger"></i></Button>
                </>
            )
        }
    ]

    return (
        <React.Fragment>
            <div className="page-content">
                <MetaTags>
                    <title>Shift Roster | HRMS</title>
                </MetaTags>
                <Container fluid>
                    <Card>
                        <CardBody>
                            <div className="d-flex align-items-center justify-content-between mb-4 border-bottom pb-2">
                                <Nav tabs className="nav-tabs-custom border-bottom-0">
                                    <NavItem>
                                        <NavLink
                                            className={classnames({ active: activeTab === "1" })}
                                            onClick={() => setActiveTab("1")}
                                            style={{ cursor: "pointer", border: "none" }}
                                        >
                                            Roster Scheduler
                                        </NavLink>
                                    </NavItem>
                                    <NavItem>
                                        <NavLink
                                            className={classnames({ active: activeTab === "2" })}
                                            onClick={() => setActiveTab("2")}
                                            style={{ cursor: "pointer", border: "none" }}
                                        >
                                            Roster List
                                        </NavLink>
                                    </NavItem>
                                    <NavItem>
                                        <NavLink
                                            className={classnames({ active: activeTab === "3" })}
                                            onClick={() => setActiveTab("3")}
                                            style={{ cursor: "pointer", border: "none" }}
                                        >
                                            Shift Master
                                        </NavLink>
                                    </NavItem>
                                    <NavItem>
                                        <NavLink
                                            className={classnames({ active: activeTab === "4" })}
                                            onClick={() => setActiveTab("4")}
                                            style={{ cursor: "pointer", border: "none" }}
                                        >
                                            Policy Rules
                                        </NavLink>
                                    </NavItem>
                                </Nav>
                                <div className="d-flex g-2">
                                    {activeTab === "1" && (
                                        <>
                                            <Button style={greenBtnStyle} className="me-2" onClick={() => toggleAssignModal(true)}>
                                                <i className="fas fa-calendar-check me-1"></i> Bulk Assign
                                            </Button>
                                            <Button style={brownBtnStyle} onClick={() => toggleAssignModal(false)}>
                                                <i className="fas fa-plus me-1"></i> Assign Shift
                                            </Button>
                                        </>
                                    )}
                                    {activeTab === "2" && (
                                        <Button style={brownBtnStyle} onClick={() => toggleAssignModal(false)}>
                                            <i className="fas fa-plus me-1"></i> Assign Shift
                                        </Button>
                                    )}
                                    {activeTab === "3" && (
                                        <Button style={brownBtnStyle} onClick={toggleShiftModal}>
                                            <i className="fas fa-plus me-1"></i> Create Shift
                                        </Button>
                                    )}
                                </div>
                            </div>
                            <TabContent activeTab={activeTab} className="p-0 text-muted">

                                {/* TAB 1: Roster Calendar/Grid */}
                                <TabPane tabId="1">
                                    <div className="text-uppercase mb-3" style={{ fontWeight: "600", fontSize: "12px", color: "#555" }}>
                                        Roster Grid Filters
                                    </div>
                                    <Row className="mb-4 g-2">
                                        <Col md={3}>
                                            <Select
                                                placeholder="Select Branch"
                                                options={branchOptions}
                                                value={branchOptions.find(b => b.value === filters.branch_id) || null}
                                                onChange={e => setFilters(prev => ({ ...prev, branch_id: e ? e.value : "" }))}
                                                styles={customStyles}
                                                isClearable
                                            />
                                        </Col>
                                        <Col md={3}>
                                            <Select
                                                placeholder="Select Employee"
                                                options={[{ value: "all", label: "All Employees" }, ...employeeOptions]}
                                                value={[{ value: "all", label: "All Employees" }, ...employeeOptions].find(emp => emp.value === filters.employee_id) || null}
                                                onChange={e => setFilters(prev => ({ ...prev, employee_id: e ? e.value : "all" }))}
                                                styles={customStyles}
                                            />
                                        </Col>
                                        <Col md={2}>
                                            <Input
                                                type="select"
                                                value={filters.month}
                                                onChange={e => setFilters(prev => ({ ...prev, month: parseInt(e.target.value) }))}
                                            >
                                                {Array.from({ length: 12 }, (_, i) => (
                                                    <option key={i + 1} value={i + 1}>
                                                        {new Date(2026, i, 1).toLocaleString("default", { month: "long" })}
                                                    </option>
                                                ))}
                                            </Input>
                                        </Col>
                                        <Col md={2}>
                                            <Input
                                                type="select"
                                                value={filters.year}
                                                onChange={e => setFilters(prev => ({ ...prev, year: parseInt(e.target.value) }))}
                                            >
                                                <option value={new Date().getFullYear() - 1}>{new Date().getFullYear() - 1}</option>
                                                <option value={new Date().getFullYear()}>{new Date().getFullYear()}</option>
                                                <option value={new Date().getFullYear() + 1}>{new Date().getFullYear() + 1}</option>
                                            </Input>
                                        </Col>
                                        <Col md={2}>
                                            <Button style={brownBtnStyle} className="w-100" onClick={() => setFilters({
                                                branch_id: "",
                                                employee_id: "all",
                                                year: new Date().getFullYear(),
                                                month: new Date().getMonth() + 1
                                            })}>Reset</Button>
                                        </Col>
                                    </Row>

                                    <div className="table-responsive border rounded" style={{ maxHeight: "600px" }}>
                                        <Table bordered hover size="sm" className="mb-0 align-middle text-center" style={{ tableLayout: "fixed", minWidth: "1200px" }}>
                                            <thead className="table-light sticky-top">
                                                <tr>
                                                    <th style={{ width: "180px", textAlign: "left", paddingLeft: "15px" }}>Employee</th>
                                                    {getDaysArray().map(d => {
                                                        const dateObj = new Date(filters.year, filters.month - 1, d)
                                                        const isSunday = dateObj.getDay() === 0
                                                        return (
                                                            <th key={d} style={{ width: "45px", backgroundColor: isSunday ? "#ffebee" : "" }}>
                                                                <div>{d}</div>
                                                                <small className="text-muted" style={{ fontSize: "9px" }}>
                                                                    {dateObj.toLocaleDateString("default", { weekday: "short" }).substring(0, 2)}
                                                                </small>
                                                            </th>
                                                        )
                                                    })}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {isLoading ? (
                                                    <tr>
                                                        <td colSpan={getDaysArray().length + 1} className="py-4 text-center">Loading roster scheduler...</td>
                                                    </tr>
                                                ) : gridData.employees.length === 0 ? (
                                                    <tr>
                                                        <td colSpan={getDaysArray().length + 1} className="py-4 text-center">No active employees found</td>
                                                    </tr>
                                                ) : (
                                                    gridData.employees.map(emp => (
                                                        <tr key={emp.code}>
                                                            <td style={{ textAlign: "left", paddingLeft: "15px", fontWeight: "600", fontSize: "12px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                                {emp.name}
                                                                <div className="text-muted" style={{ fontSize: "9px" }}>Code: {emp.code}</div>
                                                            </td>
                                                            {getDaysArray().map(d => {
                                                                const dateStr = `${filters.year}-${pad(filters.month)}-${pad(d)}`
                                                                const existing = gridData.grid[emp.code]?.[dateStr]
                                                                return (
                                                                    <td
                                                                        key={d}
                                                                        onClick={() => handleGridCellClick(emp, dateStr, existing)}
                                                                        style={{ cursor: "pointer", height: "45px", position: "relative" }}
                                                                        className="hover-cell"
                                                                    >
                                                                        {existing ? (
                                                                            <span style={getShiftBadgeStyle(existing.shift_name)}>
                                                                                {existing.shift_name.substring(0, 3)}
                                                                            </span>
                                                                        ) : (
                                                                            <span className="text-muted opacity-0 hover-opacity" style={{ fontSize: "14px" }}>+</span>
                                                                        )}
                                                                    </td>
                                                                )
                                                            })}
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </Table>
                                    </div>
                                </TabPane>

                                {/* TAB 2: Roster List (Datatable) */}
                                <TabPane tabId="2">
                                    <div className="text-uppercase mb-3" style={{ fontWeight: "600", fontSize: "12px", color: "#555" }}>
                                        Roster Filters
                                    </div>
                                    <Row className="mb-4 g-2">
                                        <Col md={3}>
                                            <Select
                                                placeholder="Select Branch"
                                                options={branchOptions}
                                                value={branchOptions.find(b => b.value === listFilters.branch_id) || null}
                                                onChange={e => setListFilters(prev => ({ ...prev, branch_id: e ? e.value : "" }))}
                                                styles={customStyles}
                                                isClearable
                                            />
                                        </Col>
                                        <Col md={3}>
                                            <Select
                                                placeholder="Select Employee"
                                                options={[{ value: "all", label: "All Employees" }, ...employeeOptions]}
                                                value={[{ value: "all", label: "All Employees" }, ...employeeOptions].find(emp => emp.value === listFilters.employee_id) || null}
                                                onChange={e => setListFilters(prev => ({ ...prev, employee_id: e ? e.value : "all" }))}
                                                styles={customStyles}
                                            />
                                        </Col>
                                        <Col md={2}>
                                            <Input
                                                type="date"
                                                placeholder="Start Date"
                                                value={listFilters.start_date}
                                                onChange={e => setListFilters(prev => ({ ...prev, start_date: e.target.value }))}
                                            />
                                        </Col>
                                        <Col md={2}>
                                            <Input
                                                type="date"
                                                placeholder="End Date"
                                                value={listFilters.end_date}
                                                onChange={e => setListFilters(prev => ({ ...prev, end_date: e.target.value }))}
                                            />
                                        </Col>
                                        <Col md={2}>
                                            <Button style={brownBtnStyle} className="w-100" onClick={() => setListFilters({
                                                branch_id: "",
                                                employee_id: "all",
                                                start_date: "",
                                                end_date: ""
                                            })}>Reset</Button>
                                        </Col>
                                    </Row>

                                    <Datatables
                                        title="Roster List Log"
                                        titleClass="h5"
                                        columns={listColumns}
                                        rows={listData.rows}
                                        keyField="id"
                                        rowsLength={listData.total_rows}
                                        isAdd={false}
                                        isSearch={true}
                                        loading={isLoading}
                                        ssr={setListPagination}
                                    />
                                </TabPane>

                                {/* TAB 3: Shift Master CRUD */}
                                <TabPane tabId="3">
                                    <Datatables
                                        title="Shift Setup Master"
                                        titleClass="h5"
                                        columns={shiftMasterColumns}
                                        rows={shiftsData.rows}
                                        keyField="id"
                                        rowsLength={shiftsData.total_rows}
                                        isAdd={false}
                                        isSearch={false}
                                        loading={isLoading}
                                    />
                                </TabPane>

                                {/* TAB 4: Rules Setup */}
                                <TabPane tabId="4">
                                    <h5 className="mb-4">Compliance Policy Rules</h5>
                                    <Form onSubmit={handleRulesSubmit}>
                                        <Row>
                                            <Col md={6}>
                                                <FormGroup className="mb-3">
                                                    <Label>Minimum Shift Hours</Label>
                                                    <Input
                                                        type="number"
                                                        value={rules.min_shift_hours}
                                                        onChange={e => setRules(prev => ({ ...prev, min_shift_hours: e.target.value }))}
                                                    />
                                                </FormGroup>
                                            </Col>
                                            <Col md={6}>
                                                <FormGroup className="mb-3">
                                                    <Label>Minimum Rest Gap (in Hours)</Label>
                                                    <Input
                                                        type="number"
                                                        value={rules.min_rest_hours}
                                                        onChange={e => setRules(prev => ({ ...prev, min_rest_hours: e.target.value }))}
                                                    />
                                                </FormGroup>
                                            </Col>
                                            <Col md={6}>
                                                <FormGroup className="mb-3">
                                                    <Label>Maximum Continuous Hours</Label>
                                                    <Input
                                                        type="number"
                                                        value={rules.max_continuous_hours}
                                                        onChange={e => setRules(prev => ({ ...prev, max_continuous_hours: e.target.value }))}
                                                    />
                                                </FormGroup>
                                            </Col>
                                            <Col md={6}>
                                                <FormGroup className="mb-3">
                                                    <Label>Monthly Required Working Hours</Label>
                                                    <Input
                                                        type="number"
                                                        value={rules.monthly_required_hours}
                                                        onChange={e => setRules(prev => ({ ...prev, monthly_required_hours: e.target.value }))}
                                                    />
                                                </FormGroup>
                                            </Col>
                                            <Col md={6}>
                                                <FormGroup className="mb-3">
                                                    <Label>Allow Double Shifts</Label>
                                                    <select
                                                        className="form-control"
                                                        value={rules.allow_double_shift}
                                                        onChange={e => setRules(prev => ({ ...prev, allow_double_shift: e.target.value }))}
                                                    >
                                                        <option value="1">Enabled</option>
                                                        <option value="0">Disabled</option>
                                                    </select>
                                                </FormGroup>
                                            </Col>
                                            <Col md={6}>
                                                <FormGroup className="mb-3">
                                                    <Label>Mandatory Rest after Double Shift</Label>
                                                    <select
                                                        className="form-control"
                                                        value={rules.mandatory_rest_after_double_shift}
                                                        onChange={e => setRules(prev => ({ ...prev, mandatory_rest_after_double_shift: e.target.value }))}
                                                    >
                                                        <option value="1">Mandatory</option>
                                                        <option value="0">No Rest Gap</option>
                                                    </select>
                                                </FormGroup>
                                            </Col>
                                        </Row>
                                        <div className="mt-4">
                                            <Button style={brownBtnStyle} type="submit" disabled={isActionLoading}>
                                                Save Policy Settings
                                            </Button>
                                        </div>
                                    </Form>
                                </TabPane>

                            </TabContent>
                        </CardBody>
                    </Card>

                </Container>
            </div>

            {/* Shift Modal CREATE/EDIT */}
            <Modal isOpen={shiftModal} toggle={toggleShiftModal} size="lg">
                <ModalHeader toggle={toggleShiftModal}>{editShiftId ? "Edit Shift Configuration" : "New Shift Definition"}</ModalHeader>
                <ModalBody>
                    <Form onSubmit={handleShiftSubmit}>
                        <Row>
                            <Col md={6}>
                                <FormGroup className="mb-3">
                                    <Label>Shift Name <span className="text-danger">*</span></Label>
                                    <Input
                                        type="text"
                                        placeholder="e.g. Morning, Evening, Night"
                                        value={shiftForm.shift_name}
                                        onChange={e => setShiftForm(prev => ({ ...prev, shift_name: e.target.value }))}
                                        required
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup className="mb-3">
                                    <Label>Shift Priority Code</Label>
                                    <Input
                                        type="number"
                                        value={shiftForm.shift_priority}
                                        onChange={e => setShiftForm(prev => ({ ...prev, shift_priority: parseInt(e.target.value) || 0 }))}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup className="mb-3">
                                    <Label>Start Time <span className="text-danger">*</span></Label>
                                    <Flatpickr
                                        className="form-control"
                                        options={{ noCalendar: true, enableTime: true, dateFormat: "H:i:S", time_24hr: true }}
                                        value={shiftForm.start_time}
                                        onChange={(date, str) => setShiftForm(prev => ({ ...prev, start_time: str }))}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup className="mb-3">
                                    <Label>End Time <span className="text-danger">*</span></Label>
                                    <Flatpickr
                                        className="form-control"
                                        options={{ noCalendar: true, enableTime: true, dateFormat: "H:i:S", time_24hr: true }}
                                        value={shiftForm.end_time}
                                        onChange={(date, str) => setShiftForm(prev => ({ ...prev, end_time: str }))}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup className="mb-3">
                                    <Label>Actual Duration (Hours)</Label>
                                    <Input
                                        type="number"
                                        step="0.1"
                                        value={shiftForm.actual_duration}
                                        onChange={e => setShiftForm(prev => ({ ...prev, actual_duration: e.target.value }))}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup className="mb-3">
                                    <Label>Minimum Working Hours</Label>
                                    <Input
                                        type="number"
                                        step="0.1"
                                        value={shiftForm.min_hours}
                                        onChange={e => setShiftForm(prev => ({ ...prev, min_hours: e.target.value }))}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup className="mb-3">
                                    <Label>Cross Midnight Day Shift?</Label>
                                    <select
                                        className="form-control"
                                        value={shiftForm.cross_day}
                                        onChange={e => setShiftForm(prev => ({ ...prev, cross_day: parseInt(e.target.value) || 0 }))}
                                    >
                                        <option value="0">No (Ends same day)</option>
                                        <option value="1">Yes (Ends next day)</option>
                                    </select>
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup className="mb-3">
                                    <Label>Status</Label>
                                    <select
                                        className="form-control"
                                        value={shiftForm.status}
                                        onChange={e => setShiftForm(prev => ({ ...prev, status: parseInt(e.target.value) || 0 }))}
                                    >
                                        <option value="1">Active</option>
                                        <option value="0">Inactive</option>
                                    </select>
                                </FormGroup>
                            </Col>
                        </Row>
                        <div className="text-end mt-4">
                            <Button color="secondary" className="me-2" onClick={toggleShiftModal}>Cancel</Button>
                            <Button style={brownBtnStyle} type="submit" disabled={isActionLoading}>
                                {editShiftId ? "Update Shift Configuration" : "Save Shift Definition"}
                            </Button>
                        </div>
                    </Form>
                </ModalBody>
            </Modal>

            {/* Roster Assignment Modal (Single / Bulk) */}
            <Modal isOpen={assignModal} toggle={toggleAssignModal} size="lg">
                <ModalHeader toggle={toggleAssignModal}>
                    {assignForm.id ? "Edit Roster Assignment" : isBulkAssign ? "Bulk Roster Scheduler" : "Assign Shift Roster"}
                </ModalHeader>
                <ModalBody>
                    <Form onSubmit={handleAssignSubmit}>
                        <Row>
                            {isBulkAssign ? (
                                <Col md={12}>
                                    <FormGroup className="mb-3">
                                        <Label>Select Employees <span className="text-danger">*</span></Label>
                                        <Select
                                            placeholder="Select Multiple Employees"
                                            options={employeeOptions.map(e => ({ value: e.value, label: e.label }))}
                                            value={assignForm.employee_ids}
                                            onChange={options => setAssignForm(prev => ({ ...prev, employee_ids: options || [] }))}
                                            isMulti
                                            required
                                        />
                                    </FormGroup>
                                </Col>
                            ) : (
                                <Col md={6}>
                                    <FormGroup className="mb-3">
                                        <Label>Select Employee <span className="text-danger">*</span></Label>
                                        <Select
                                            placeholder="Select Employee"
                                            options={employeeOptions.map(e => ({ value: e.value, label: e.label }))}
                                            value={assignForm.employee_id}
                                            onChange={option => setAssignForm(prev => ({ ...prev, employee_id: option }))}
                                            isDisabled={!!assignForm.id}
                                            required
                                        />
                                    </FormGroup>
                                </Col>
                            )}

                            <Col md={isBulkAssign ? 12 : 6}>
                                <FormGroup className="mb-3">
                                    <Label>Select Shift Master <span className="text-danger">*</span></Label>
                                    <Select
                                        placeholder="Select Shift"
                                        options={shiftsData.rows.map(s => ({ value: s.id, label: `${s.shift_name} (${s.start_time}-${s.end_time})` }))}
                                        value={shiftsData.rows.map(s => ({ value: s.id, label: `${s.shift_name} (${s.start_time}-${s.end_time})` })).find(s => s.value == assignForm.shift_id) || assignForm.shift_id}
                                        onChange={option => setAssignForm(prev => ({ ...prev, shift_id: option }))}
                                        required
                                    />
                                </FormGroup>
                            </Col>

                            {isBulkAssign ? (
                                <>
                                    <Col md={6}>
                                        <FormGroup className="mb-3">
                                            <Label>From Date <span className="text-danger">*</span></Label>
                                            <Flatpickr
                                                className="form-control"
                                                options={{ dateFormat: "Y-m-d" }}
                                                value={assignForm.start_date}
                                                onChange={(date, str) => setAssignForm(prev => ({ ...prev, start_date: str }))}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md={6}>
                                        <FormGroup className="mb-3">
                                            <Label>To Date <span className="text-danger">*</span></Label>
                                            <Flatpickr
                                                className="form-control"
                                                options={{ dateFormat: "Y-m-d" }}
                                                value={assignForm.end_date}
                                                onChange={(date, str) => setAssignForm(prev => ({ ...prev, end_date: str }))}
                                            />
                                        </FormGroup>
                                    </Col>
                                </>
                            ) : (
                                <Col md={6}>
                                    <FormGroup className="mb-3">
                                        <Label>Shift Date <span className="text-danger">*</span></Label>
                                        <Flatpickr
                                            className="form-control"
                                            options={{ dateFormat: "Y-m-d" }}
                                            value={assignForm.shift_date}
                                            disabled={!!assignForm.id}
                                            onChange={(date, str) => setAssignForm(prev => ({ ...prev, shift_date: str }))}
                                        />
                                    </FormGroup>
                                </Col>
                            )}

                            <Col md={6}>
                                <FormGroup className="mb-3">
                                    <Label>Roster Status</Label>
                                    <select
                                        className="form-control"
                                        value={assignForm.status}
                                        onChange={e => setAssignForm(prev => ({ ...prev, status: e.target.value }))}
                                    >
                                        <option value="Assigned">Assigned</option>
                                        <option value="Completed">Completed</option>
                                        <option value="Leave">Leave</option>
                                        <option value="Off">Off</option>
                                    </select>
                                </FormGroup>
                            </Col>

                            {!isBulkAssign && (
                                <Col md={6}>
                                    <FormGroup className="mb-3">
                                        <Label>Is Double Shift?</Label>
                                        <select
                                            className="form-control"
                                            value={assignForm.is_double_shift}
                                            onChange={e => setAssignForm(prev => ({ ...prev, is_double_shift: parseInt(e.target.value) || 0 }))}
                                        >
                                            <option value="0">No</option>
                                            <option value="1">Yes</option>
                                        </select>
                                    </FormGroup>
                                </Col>
                            )}
                        </Row>
                        <div className="text-end mt-4">
                            <Button color="secondary" className="me-2" onClick={toggleAssignModal}>Cancel</Button>
                            <Button style={brownBtnStyle} type="submit" disabled={isActionLoading}>
                                {assignForm.id ? "Update Assignment" : "Assign Shift"}
                            </Button>
                        </div>
                    </Form>
                </ModalBody>
            </Modal>

            {/* Micro-animations and custom hover css rules */}
            <style dangerouslySetInnerHTML={{
                __html: `
                .hover-cell:hover {
                    background-color: #f5f5f5 !important;
                    transition: background-color 0.2s ease;
                }
                .hover-opacity {
                    opacity: 0;
                    transition: opacity 0.2s ease;
                }
                .hover-cell:hover .hover-opacity {
                    opacity: 0.8 !important;
                }
            `}} />
        </React.Fragment>
    )
}

export default Roster
